import { useEffect } from 'react';

/** Marks shell UI for `useInertBackground` (layout, drawer, skip link). */
export const APP_SHELL_INERT_ATTR = 'data-app-shell-inert' as const;

/** Spread onto shell surfaces that should become inert when a blocking modal is open. */
export const appShellInertDomProps = { [APP_SHELL_INERT_ATTR]: '' } as const;

/**
 * Applies the `inert` attribute to every element marked with `[data-app-shell-inert]`
 * for as long as this hook is mounted.
 *
 * Call this inside modal dialogs so keyboard and screen-reader users cannot reach
 * content behind the dialog (WCAG 2.4.3 / modal dialog pattern; QUIZ-456).
 *
 * ## Why not only `#app-layout-container`?
 * In the default app shell (`Layout.tsx`), the navigation drawer is a **sibling** of
 * `LayoutContainer`, not a descendant. Inerting only the main column leaves the drawer
 * (and the skip link) focusable, so Tab can escape the modal toward browser chrome.
 *
 * MUI portals dialogs into `#root` as siblings of the shell, so inerting shell nodes
 * does not inert the dialog itself.
 *
 * Requires the `wicg-inert` polyfill (imported in App.tsx) where native `inert` is missing.
 */
export function useInertBackground(): void {
  useEffect(() => {
    const elements = Array.from(
      document.querySelectorAll<HTMLElement>(`[${APP_SHELL_INERT_ATTR}]`)
    );

    elements.forEach((el) => {
      el.setAttribute('inert', '');
    });

    return () => {
      elements.forEach((el) => {
        el.removeAttribute('inert');
      });
    };
  }, []);
}
